/**
 * Dashboard Slice
 * 
 * Manages dashboard data and statistics for both CVO and Authority roles
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dashboardService from '../../services/dashboardService';

// Initial state
const initialState = {
  // CVO Dashboard Data
  cvoDashboard: {
    data: null,
    isLoading: false,
    error: null,
  },
  
  // Authority Dashboard Data
  authorityDashboard: {
    data: null,
    isLoading: false,
    error: null,
  },
  
  // General Statistics
  stats: {
    data: null,
    isLoading: false,
    error: null,
  },
  
  // Recent Activity
  recentActivity: {
    data: null,
    isLoading: false,
    error: null,
  },
};

// Async thunks
export const fetchCVODashboard = createAsyncThunk(
  'dashboard/fetchCVODashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getCVODashboard();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch CVO dashboard');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAuthorityDashboard = createAsyncThunk(
  'dashboard/fetchAuthorityDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getAuthorityDashboard();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch Authority dashboard');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getStats(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch statistics');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCVOStats = createAsyncThunk(
  'dashboard/fetchCVOStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getCVOStats(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch CVO statistics');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAuthorityStats = createAsyncThunk(
  'dashboard/fetchAuthorityStats',
  async (params, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getAuthorityStats(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch Authority statistics');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRecentActivity = createAsyncThunk(
  'dashboard/fetchRecentActivity',
  async (params, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getRecentActivity(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch recent activity');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Clear dashboard data
    clearDashboard: (state) => {
      state.cvoDashboard = initialState.cvoDashboard;
      state.authorityDashboard = initialState.authorityDashboard;
      state.stats = initialState.stats;
      state.recentActivity = initialState.recentActivity;
    },
    
    // Clear specific dashboard data
    clearCVODashboard: (state) => {
      state.cvoDashboard = initialState.cvoDashboard;
    },
    
    clearAuthorityDashboard: (state) => {
      state.authorityDashboard = initialState.authorityDashboard;
    },
    
    // Clear errors
    clearDashboardError: (state) => {
      state.cvoDashboard.error = null;
      state.authorityDashboard.error = null;
      state.stats.error = null;
      state.recentActivity.error = null;
    },
  },
  extraReducers: (builder) => {
    // CVO Dashboard
    builder
      .addCase(fetchCVODashboard.pending, (state) => {
        state.cvoDashboard.isLoading = true;
        state.cvoDashboard.error = null;
      })
      .addCase(fetchCVODashboard.fulfilled, (state, action) => {
        state.cvoDashboard.isLoading = false;
        state.cvoDashboard.data = action.payload;
        state.cvoDashboard.error = null;
      })
      .addCase(fetchCVODashboard.rejected, (state, action) => {
        state.cvoDashboard.isLoading = false;
        state.cvoDashboard.error = action.payload;
      });

    // Authority Dashboard
    builder
      .addCase(fetchAuthorityDashboard.pending, (state) => {
        state.authorityDashboard.isLoading = true;
        state.authorityDashboard.error = null;
      })
      .addCase(fetchAuthorityDashboard.fulfilled, (state, action) => {
        state.authorityDashboard.isLoading = false;
        state.authorityDashboard.data = action.payload;
        state.authorityDashboard.error = null;
      })
      .addCase(fetchAuthorityDashboard.rejected, (state, action) => {
        state.authorityDashboard.isLoading = false;
        state.authorityDashboard.error = action.payload;
      });

    // General Stats
    builder
      .addCase(fetchStats.pending, (state) => {
        state.stats.isLoading = true;
        state.stats.error = null;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats.isLoading = false;
        state.stats.data = action.payload;
        state.stats.error = null;
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.stats.isLoading = false;
        state.stats.error = action.payload;
      });

    // CVO Stats
    builder
      .addCase(fetchCVOStats.pending, (state) => {
        state.stats.isLoading = true;
        state.stats.error = null;
      })
      .addCase(fetchCVOStats.fulfilled, (state, action) => {
        state.stats.isLoading = false;
        state.stats.data = action.payload;
        state.stats.error = null;
      })
      .addCase(fetchCVOStats.rejected, (state, action) => {
        state.stats.isLoading = false;
        state.stats.error = action.payload;
      });

    // Authority Stats
    builder
      .addCase(fetchAuthorityStats.pending, (state) => {
        state.stats.isLoading = true;
        state.stats.error = null;
      })
      .addCase(fetchAuthorityStats.fulfilled, (state, action) => {
        state.stats.isLoading = false;
        state.stats.data = action.payload;
        state.stats.error = null;
      })
      .addCase(fetchAuthorityStats.rejected, (state, action) => {
        state.stats.isLoading = false;
        state.stats.error = action.payload;
      });

    // Recent Activity
    builder
      .addCase(fetchRecentActivity.pending, (state) => {
        state.recentActivity.isLoading = true;
        state.recentActivity.error = null;
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        state.recentActivity.isLoading = false;
        state.recentActivity.data = action.payload;
        state.recentActivity.error = null;
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        state.recentActivity.isLoading = false;
        state.recentActivity.error = action.payload;
      });
  },
});

// Export actions
export const { 
  clearDashboard, 
  clearCVODashboard, 
  clearAuthorityDashboard, 
  clearDashboardError 
} = dashboardSlice.actions;

// Export selectors
export const selectCVODashboard = (state) => state.dashboard.cvoDashboard;
export const selectAuthorityDashboard = (state) => state.dashboard.authorityDashboard;
export const selectDashboardStats = (state) => state.dashboard.stats;
export const selectRecentActivity = (state) => state.dashboard.recentActivity;

// Export reducer
export default dashboardSlice.reducer;
